/**
 * DialogueRunner — walks a DialogueData graph through an IDialogueBox.
 *
 * Phaser-free (unit-testable). Presentation lives in the box; state lives behind `DialogueHooks`.
 *
 * Contract highlights
 * - A missing dialogue id (or missing start/next node) never soft-locks: `run()` warns and resolves.
 * - `advance()` = the player's confirm press: completes the typewriter while typing, otherwise moves
 *   to the next line. During choices the box owns the input and `advance()` is ignored.
 * - When the box implements the optional `waitForAdvance()` (the real UI box, which listens to
 *   `input:confirm` itself) the runner races it with `advance()`/`abort()`, so keyboard, pointer and
 *   touch all work whichever layer receives them first.
 * - Conditions (`hasFlag/notFlag/hasMark/notMark/weather`) skip the node to `elseNext ?? next`.
 * - `setMark`/`setFlag` on a node apply after the line has been confirmed (or immediately when the
 *   line is skipped as already-read), so state converges regardless of skipping.
 * - `skipReadLines`: lines whose text hash is already marked as `read:<hash>` are not shown again
 *   (lines with choices are always shown). Every shown line is marked read.
 * - Fast-forward: while `input:fastText` is held (Shift/Ctrl, see characters/InputMap) or after
 *   `setFastHeld(true)`, lines appear instantly and auto-advance every `fastHoldMs` (choices still wait).
 * - `run()` while another dialogue is running QUEUES the request (a cinematic `say` colliding with a
 *   talk waits for the talk to finish, then shows its lines — a mandatory quote is never dropped).
 *   Queued runs resolve in order; `abort()` drops the queue (their promises resolve without showing
 *   anything — the skipping cinematic applies their state through `fastForward`).
 * - `abort()` ends the current run early (used by cinematic skip / scene shutdown).
 * - `fastForward(id)` applies a dialogue's marks/flags silently (used when a cinematic is skipped
 *   past a `say` step). It resumes from the node the last `abort()` interrupted, so a partially
 *   played and a fully played dialogue end in the same state; otherwise it walks from the start
 *   (conditions honoured, choices resolve to their first option).
 */
import type { DialogueData, DialogueNode, IDialogueBox, IDialogueRunner } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { bus, Events } from '@/core/events';
import { evalCondition } from './conditions';
import { readMarkFor } from './hash';
import { getDialogue } from './registry';
import { isStoryFlag } from '@/story/flags';
import type { GameContext } from '@/core/context';

export interface DialogueHooks {
  hasFlag(flag: string): boolean;
  hasMark(mark: string): boolean;
  setMark(mark: string): void;
  setFlag(flag: string): void;
  weather(): string;
  instant(): boolean;
}

/** Optional presentation callbacks (actor emotes/facing, sfx) — supplied by the owning scene. */
export interface DialogueHost {
  emote?(actor: string, emote: string): void;
  face?(actor: string, facing: Facing): void;
  sfx?(key: string): void;
  /** Called for every line that is actually shown (after conditions / read-skip). */
  onLine?(node: DialogueNode, dialogueId: string): void;
  /** Scene timer used for the fast-forward cadence (defaults to "next microtask" when absent). */
  wait?(ms: number): Promise<void>;
}

export interface DialogueRunnerOptions {
  host?: DialogueHost;
  /** Guard against cyclic data: max nodes visited per run. */
  maxSteps?: number;
  skipReadLines?: boolean;
  /** Where dialogues come from; defaults to the module registry. */
  lookup?: (id: string) => DialogueData | undefined;
  /** Follow the `input:fastText` bus event while running (default true). */
  fastTextFromBus?: boolean;
  /** Milliseconds between auto-advanced lines while fast-forward is held (default 90). */
  fastHoldMs?: number;
}

export type DialoguePhase = 'idle' | 'typing' | 'waiting' | 'choices';

export class DialogueRunner implements IDialogueRunner {
  skipReadLines: boolean;
  private phase: DialoguePhase = 'idle';
  private running = false;
  private aborted = false;
  private fastHeld = false;
  private currentId: string | null = null;
  private cursor: { id: string; nodeId: string } | null = null;
  private abortedAt: { id: string; nodeId: string } | null = null;
  private pendingAdvance: (() => void) | null = null;
  private readonly abortFns = new Set<() => void>();
  /** Dialogues requested while another one runs; started in order when the current run ends. */
  private readonly queue: { id: string; start: () => void; drop: () => void }[] = [];
  private readonly host: DialogueHost;
  private readonly maxSteps: number;
  private readonly lookup: (id: string) => DialogueData | undefined;
  private readonly fastTextFromBus: boolean;
  private readonly fastHoldMs: number;
  private readonly onFastText = (p: { held: boolean } | undefined) => { this.setFastHeld(!!p?.held); };

  constructor(private readonly box: IDialogueBox, private readonly hooks: DialogueHooks, opts: DialogueRunnerOptions = {}) {
    this.host = opts.host ?? {};
    this.maxSteps = opts.maxSteps ?? 500;
    this.skipReadLines = opts.skipReadLines ?? false;
    this.lookup = opts.lookup ?? getDialogue;
    this.fastTextFromBus = opts.fastTextFromBus ?? true;
    this.fastHoldMs = opts.fastHoldMs ?? 90;
  }

  isRunning(): boolean { return this.running; }
  /** Number of dialogues waiting behind the current run. */
  queued(): number { return this.queue.length; }
  getPhase(): DialoguePhase { return this.phase; }
  currentDialogue(): string | null { return this.currentId; }
  isFastHeld(): boolean { return this.fastHeld; }

  /** Player pressed confirm. */
  advance(): void {
    if (!this.running) return;
    if (this.phase === 'typing') { this.box.completeTyping(); return; }
    if (this.phase === 'waiting' && this.pendingAdvance) {
      const resolve = this.pendingAdvance;
      this.pendingAdvance = null;
      resolve();
    }
  }

  /** Fast-forward held (Shift/Ctrl): lines appear instantly and auto-advance; choices still wait. */
  setFastHeld(held: boolean): void {
    if (this.fastHeld === held) return;
    this.fastHeld = held;
    if (held && this.running) this.advance();
  }

  /** End the current run immediately (cinematic skip, scene shutdown). Safe when idle. */
  abort(): void {
    for (const q of this.queue.splice(0)) { console.warn(`[dialogue] queued "${q.id}" dropped by abort`); q.drop(); }
    if (!this.running) return;
    this.aborted = true;
    this.abortedAt = this.cursor;
    if (this.phase === 'typing') { try { this.box.completeTyping(); } catch { /* ignore */ } }
    for (const f of [...this.abortFns]) f();
    this.abortFns.clear();
    if (this.pendingAdvance) { const r = this.pendingAdvance; this.pendingAdvance = null; r(); }
  }

  async run(dialogueId: string): Promise<void> {
    const data = this.lookup(dialogueId);
    if (!data) { console.warn(`[dialogue] missing dialogue "${dialogueId}" — skipped`); return; }
    if (this.running) {
      // Collision (e.g. a cinematic `say` while a talk is open): wait our turn instead of losing the lines.
      console.warn(`[dialogue] "${dialogueId}" requested while "${this.currentId}" is running — queued`);
      const turn = new Promise<boolean>((resolve) => this.queue.push({ id: dialogueId, start: () => resolve(true), drop: () => resolve(false) }));
      if (!(await turn)) return;
      if (this.running) { console.warn(`[dialogue] "${dialogueId}" still blocked — skipped`); return; }
      return this.run(dialogueId);
    }
    this.running = true;
    this.aborted = false;
    this.abortedAt = null;
    this.cursor = null;
    this.currentId = dialogueId;
    if (this.fastTextFromBus) bus.onTyped(Events.INPUT_FAST_TEXT, this.onFastText);
    bus.emitTyped(Events.DIALOGUE_START, { id: dialogueId });
    try {
      await this.walk(data);
    } catch (e) {
      console.error(`[dialogue] error while running "${dialogueId}"`, e);
    } finally {
      if (this.fastTextFromBus) bus.offTyped(Events.INPUT_FAST_TEXT, this.onFastText);
      try { await this.box.hide(); } catch { /* ignore */ }
      this.running = false;
      this.phase = 'idle';
      this.currentId = null;
      this.cursor = null;
      this.pendingAdvance = null;
      this.abortFns.clear();
      bus.emitTyped(Events.DIALOGUE_END, { id: dialogueId });
      // Hand over to the next queued dialogue (it re-enters run() with `running` false).
      this.queue.shift()?.start();
    }
  }

  /**
   * Apply a dialogue's state effects without showing it (cinematic skip). Conditions are honoured,
   * choices take their first option. Resumes from the node an `abort()` interrupted when it is the
   * same dialogue, so it is idempotent with a partially played run.
   */
  fastForward(dialogueId: string): void {
    const data = this.lookup(dialogueId);
    if (!data) { console.warn(`[dialogue] fastForward: missing dialogue "${dialogueId}"`); return; }
    const byId = this.index(data);
    const resume = this.abortedAt?.id === dialogueId ? this.abortedAt.nodeId : null;
    this.abortedAt = null;
    let node = byId.get(resume ?? data.start);
    if (!node) { console.warn(`[dialogue] fastForward "${dialogueId}": node "${resume ?? data.start}" not found`); return; }
    let steps = 0;
    while (node && ++steps <= this.maxSteps) {
      if (!evalCondition(node.condition, this.hooks)) { node = this.follow(byId, node.elseNext ?? node.next, data.id); continue; }
      let nextId = node.next;
      const first = node.choices?.[0];
      if (first) { if (first.setMark) this.hooks.setMark(first.setMark); nextId = first.next; }
      if (node.setMark) this.hooks.setMark(node.setMark);
      if (node.setFlag) this.hooks.setFlag(node.setFlag);
      node = this.follow(byId, nextId, data.id);
    }
  }

  private index(data: DialogueData): Map<string, DialogueNode> {
    const byId = new Map<string, DialogueNode>();
    for (const n of data.nodes) byId.set(n.id, n);
    return byId;
  }

  private follow(byId: Map<string, DialogueNode>, nextId: string | undefined, dialogueId: string): DialogueNode | undefined {
    if (!nextId) return undefined;
    const n = byId.get(nextId);
    if (!n) console.warn(`[dialogue] "${dialogueId}": node "${nextId}" not found — ending`);
    return n;
  }

  private async walk(data: DialogueData): Promise<void> {
    const byId = this.index(data);
    let node = byId.get(data.start);
    if (!node) { console.warn(`[dialogue] "${data.id}": start node "${data.start}" not found`); return; }
    let steps = 0;
    while (node && !this.aborted) {
      if (++steps > this.maxSteps) { console.warn(`[dialogue] "${data.id}": exceeded ${this.maxSteps} steps (cycle?) — ending`); break; }
      this.cursor = { id: data.id, nodeId: node.id };
      if (!evalCondition(node.condition, this.hooks)) { node = this.follow(byId, node.elseNext ?? node.next, data.id); continue; }

      const choices = node.choices ?? [];
      const hasChoices = choices.length > 0;
      const alreadyRead = this.skipReadLines && !hasChoices && this.hooks.hasMark(readMarkFor(node.text));
      let nextId = node.next;

      if (!alreadyRead) {
        if (node.face) this.host.face?.(node.face.actor, node.face.facing);
        if (node.emote) this.host.emote?.(node.emote.actor, node.emote.emote);
        if (node.sfx) this.host.sfx?.(node.sfx);
        this.host.onLine?.(node, data.id);

        this.phase = 'typing';
        const line = { speaker: node.speaker, portrait: node.portrait, emotion: node.emotion, text: node.text };
        await this.interruptible(this.box.show(line, { instant: this.hooks.instant() || this.fastHeld }));
        if (this.aborted) break;
        this.box.pushHistory(node.speaker, node.text);
        bus.emitTyped(Events.DIALOGUE_LINE, { id: data.id, speaker: node.speaker ?? '', text: node.text });
        this.hooks.setMark(readMarkFor(node.text));

        if (hasChoices) {
          this.phase = 'choices';
          const picked = await this.interruptible(this.box.showChoices(choices.map((c) => ({ text: c.text }))));
          if (this.aborted) break;
          const idx = typeof picked === 'number' && picked >= 0 && picked < choices.length ? Math.floor(picked) : 0;
          const choice = choices[idx] ?? choices[0];
          if (choice) {
            if (choice.setMark) this.hooks.setMark(choice.setMark);
            nextId = choice.next;
          }
        } else {
          this.phase = 'waiting';
          await this.waitAdvance();
          if (this.aborted) break;
        }
      }

      if (node.setMark) this.hooks.setMark(node.setMark);
      if (node.setFlag) this.hooks.setFlag(node.setFlag);
      node = this.follow(byId, nextId, data.id);
    }
  }

  /**
   * Resolves on the first of: `advance()`, `abort()`, the box's own `waitForAdvance()` (when it has
   * one), or the fast-forward cadence while fast-forward is held. Late resolutions are ignored.
   */
  private waitAdvance(): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => { if (done) return; done = true; if (this.pendingAdvance === finish) this.pendingAdvance = null; resolve(); };
      this.pendingAdvance = finish;
      if (this.box.waitForAdvance) {
        try { this.box.waitForAdvance().then(finish, finish); } catch (e) { console.warn('[dialogue] waitForAdvance threw', e); }
      }
      if (this.fastHeld) {
        const cadence = this.host.wait ? this.host.wait(this.fastHoldMs) : Promise.resolve();
        cadence.then(() => { if (this.fastHeld) finish(); }, () => { /* ignore */ });
      }
    });
  }

  /** Awaits `p` but also resolves (with undefined) when `abort()` is called. */
  private interruptible<T>(p: Promise<T>): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve, reject) => {
      const onAbort = () => resolve(undefined);
      this.abortFns.add(onAbort);
      p.then((v) => { this.abortFns.delete(onAbort); resolve(v); }, (e) => { this.abortFns.delete(onAbort); reject(e); });
    });
  }
}

/** Standard hooks bound to the shared GameContext (story flags/marks, weather, text-speed setting). */
export function hooksFromContext(ctx: GameContext): DialogueHooks {
  return {
    hasFlag: (f) => isStoryFlag(f) && ctx.story.has(f),
    hasMark: (m) => ctx.story.hasMark(m),
    setMark: (m) => void ctx.story.mark(m),
    setFlag: (f) => { if (isStoryFlag(f)) ctx.story.advance(f); else console.warn(`[dialogue] unknown flag "${f}"`); },
    weather: () => ctx.weather,
    instant: () => ctx.settings.textSpeed === 'instant',
  };
}
